export const buildRoleWorkspaceModules = (roleKey, auth) => {
  const modulesByRole = {
    finance_manager: [
      {
        title: 'Financial Reports',
        description: 'Access organization financial reports and trends.',
        link: '/history-analytics',
        allowed: () => auth.canViewReports,
      },
      {
        title: 'Revenue / Expense Analytics',
        description: 'Track income, expense, and profitability insights.',
        link: '/history-analytics',
        allowed: () => auth.canViewAnalytics,
      },
      {
        title: 'Transaction Monitoring',
        description: 'Review transaction activity across active projects.',
        link: '/transactions',
        allowed: () => auth.canViewTransactions,
      },
      {
        title: 'Export Reports',
        description: 'Generate export-ready report outputs for management.',
        link: '/history-analytics',
        allowed: () => auth.canExportReports || auth.canExportTransactions,
      },
      {
        title: 'Project Finance',
        description: 'Review project-level finance visibility and balances.',
        link: '/dashboard',
        allowed: () => auth.canManageFinance || auth.canViewProjectHistory,
      },
    ],
    accountant: [
      {
        title: 'Transactions',
        description: 'Create and update accounting entries.',
        link: '/transactions',
        allowed: () => auth.canViewTransactions || auth.canCreateTransactions || auth.canEditTransactions,
      },
      {
        title: 'Categories',
        description: 'Manage category taxonomy for bookkeeping.',
        link: '/management',
        allowed: () => auth.canManageCategories,
      },
      {
        title: 'Persons',
        description: 'Maintain person/vendor records for entries.',
        link: '/management',
        allowed: () => auth.canManagePersons,
      },
      {
        title: 'Transaction History',
        description: 'Review transaction history and balances.',
        link: '/history-analytics',
        allowed: () => auth.canViewProjectHistory || auth.canViewTransactions,
      },
      {
        title: 'Receipts',
        description: 'Upload and review supporting bills and receipts.',
        link: '/transactions',
        allowed: () => auth.canCreateTransactions || auth.canEditTransactions,
      },
    ],
    project_manager: [
      {
        title: 'Assigned Projects',
        description: 'Track assigned projects and delivery scope.',
        link: '/dashboard',
        allowed: () => auth.canViewProjects,
      },
      {
        title: 'Project Transactions',
        description: 'Monitor financial flow per active project.',
        link: '/transactions',
        allowed: () => auth.canViewTransactions,
      },
      {
        title: 'Project History',
        description: 'Review timeline and history changes.',
        link: '/history-analytics',
        allowed: () => auth.canViewProjectHistory,
      },
      {
        title: 'Team Progress',
        description: 'Observe team contribution trends and progress status.',
        link: '/organization/members',
        allowed: () => auth.canViewAnalytics,
      },
    ],
    employee: [
      {
        title: 'My Projects',
        description: 'Open your assigned project workspace.',
        link: '/dashboard',
        allowed: () => auth.canViewProjects,
      },
      {
        title: 'Add Transactions',
        description: 'Create your project transaction entries.',
        link: '/transactions',
        allowed: () => auth.canCreateTransactions,
      },
      {
        title: 'My History',
        description: 'Review personal project transaction history.',
        link: '/history-analytics',
        allowed: () => auth.canViewProjectHistory,
      },
      {
        title: 'Profile',
        description: 'Manage your account profile and settings.',
        link: '/profile',
        allowed: () => true,
      },
    ],
    viewer: [
      {
        title: 'Read-only Reports',
        description: 'View approved organization reports in read-only mode.',
        link: '/history-analytics',
        allowed: () => auth.canViewReports,
      },
      {
        title: 'Allowed Dashboards',
        description: 'Access non-edit dashboards and trend views.',
        link: '/viewer/reports',
        allowed: () => auth.canViewDashboardCards,
      },
      {
        title: 'Audit & Logs',
        description: 'Inspect permission-granted audit records.',
        link: '/history-analytics',
        allowed: () => auth.canViewAuditLogs,
      },
    ],
  };

  return (modulesByRole[roleKey] || [])
    .filter((module) => module.allowed())
    .map(({ allowed, ...module }) => module);
};

export const buildRoleWorkspaceNavItems = (roleKey, auth) => {
  const navByRole = {
    finance_manager: [
      { to: '/finance', label: 'Finance Dashboard', exact: true, permission: ['view_dashboard_cards', 'view_analytics'] },
      { to: '/finance/reports', label: 'Financial Reports', permission: ['view_reports'] },
      { to: '/finance/monitoring', label: 'Transaction Monitoring', permission: ['view_transactions'] },
      { to: '/finance/exports', label: 'Export Reports', permission: ['export_reports'] },
      { to: '/profile', label: 'Profile', alwaysVisible: true },
    ],
    accountant: [
      { to: '/accountant', label: 'Accountant Dashboard', exact: true, permission: ['view_dashboard_cards', 'view_transactions'] },
      { to: '/accountant/transactions', label: 'Transactions', permission: ['view_transactions'] },
      { to: '/accountant/categories', label: 'Categories', permission: ['manage_categories'] },
      { to: '/accountant/persons', label: 'Persons', permission: ['manage_persons'] },
      { to: '/profile', label: 'Profile', alwaysVisible: true },
    ],
    project_manager: [
      { to: '/project-manager', label: 'Project Dashboard', exact: true, permission: ['view_dashboard_cards', 'view_projects'] },
      { to: '/project-manager/projects', label: 'Assigned Projects', permission: ['view_projects'] },
      { to: '/project-manager/history', label: 'Project History', permission: ['view_project_history'] },
      { to: '/project-manager/progress', label: 'Team Progress', permission: ['view_analytics'] },
      { to: '/profile', label: 'Profile', alwaysVisible: true },
    ],
    viewer: [
      { to: '/viewer', label: 'Viewer Dashboard', exact: true, permission: ['view_dashboard_cards', 'view_reports'] },
      { to: '/viewer/reports', label: 'Read-only Reports', permission: ['view_reports'] },
      { to: '/viewer/overview', label: 'Allowed Overview', permission: ['view_dashboard_cards'] },
      { to: '/profile', label: 'Profile', alwaysVisible: true },
    ],
    employee: [
      { to: '/dashboard', label: 'My Projects', exact: true, permission: ['view_dashboard_cards', 'view_projects'] },
      { to: '/transactions', label: 'Add Transactions', permission: ['view_transactions', 'create_transactions'] },
      { to: '/history-analytics', label: 'My History', permission: ['view_project_history'] },
      { to: '/profile', label: 'Profile', alwaysVisible: true },
    ],
  };

  const items = navByRole[roleKey] || [];
  return items.filter((item) => {
    if (item.alwaysVisible) return true;
    if (!item.permission || item.permission.length === 0) return true;
    return auth.hasAnyPermission(item.permission);
  });
};
